import {reportBug} from '@packages/utilities/reporter';
import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {
  EList,
  EElement,
  ERecord,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {NO_PSEUDO} from '@packages/systems/core/utils/StyleUtils';
import {generateGuid} from '@packages/utilities/guid';
import type {WebflowCssProperties} from '@packages/systems/core/utils/WebflowCssSubset';
import type {BreakpointID, CssPropertyMap} from '@packages/systems/style/types';
import {
  getChildren,
  handleSlotFieldRemoval,
  updateComponentRenderInComponentMap,
  updateElementLocalStyle,
} from '@packages/utilities/platform';
import {isLayout} from '@packages/utilities/elementTypeUtils';
import {selectedElementContainsSlot} from '@packages/utilities/platform-elements';
import {getStyleProperties} from '@packages/systems/style/utils';

import {NEW_CELL_PREFIX} from '../constants';
import computeLayoutStructure from './computeLayoutStructure';
import type {LayoutMatrix, LayoutStructure} from '../types';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';

const PLACEHOLDER_STYLES = {
  gridColumnEnd: 'span 1',
  gridColumnStart: 'span 1',
  gridRowEnd: 'span 1',
  gridRowStart: 'span 1',
} as const;

type UpdateLayoutRenderOpts = {
  layoutElementId: string;
  targetLayoutMatrix?: LayoutMatrix;
  targetLayoutStyles?: WebflowCssProperties;
  componentMap: Component.ComponentMap;
  componentType: Plugins.QualifiedElement;
  mediaQuery: BreakpointID;
  variantState: VariantStateKey | null;
  cssPropertyMap: CssPropertyMap;
  plugins: Plugins.PluginsType;
};

/**
 * Given a Layout element id and a target layout matrix (and needed
 * component map dependencies), it updates the Layout element by:
 * - computing the target layout structure based on the matrix
 * - deleting children Cell elements that don't exist anymore in the matrix
 * - updating local styles of children Cell elements with current row and column spans
 * - re-ordering children Cell elements based on the target position
 */
const updateLayoutRenderInComponentMap = ({
  layoutElementId,
  targetLayoutMatrix,
  targetLayoutStyles,
  componentMap,
  componentType,
  mediaQuery,
  variantState,
  cssPropertyMap,
  plugins,
}: UpdateLayoutRenderOpts): Component.ComponentMap => {
  if (!targetLayoutMatrix && !targetLayoutStyles) {
    reportError('Nothing to update');
    return componentMap;
  }

  const component = componentMap.get(componentType);
  if (!component) {
    // Something went wrong, let's abort and return the original componentMap
    reportError('Component not found');
    return componentMap;
  }
  const render = Component.getRender(component);

  const isCellHidden = (cellElement: ExpressionElement) => {
    const styleProperties =
      getStyleProperties(mediaQuery)(NO_PSEUDO)(variantState)(cssPropertyMap)(
        cellElement
      );

    return styleProperties.get('display', undefined) === 'none';
  };

  const cellsWithSlotsToRemove: ExpressionElement[] = [];
  const nextRender = Expressions.updateElement(
    layoutElementId,
    function (layoutElement) {
      if (!isLayout(layoutElement)) {
        // Something went wrong, let's abort and return the original layoutElement
        reportError('Element is not Layout');
        return layoutElement;
      }

      // Update local styles if any are passed
      const nextLayoutElement = targetLayoutStyles
        ? updateElementWithStyles(
            layoutElement,
            targetLayoutStyles,
            mediaQuery,
            variantState
          )
        : layoutElement;

      if (!targetLayoutMatrix) {
        // If no target matrix is passed we can return early
        return nextLayoutElement;
      }

      const children = getChildren(nextLayoutElement);
      if (!children || children.length === 0) {
        // Something went wrong, let's abort and return the original layoutElement
        reportError('Layout has no children');
        return nextLayoutElement;
      }

      // First let's compute the desired layout structure based on the target matrix
      const layoutStructure = computeLayoutStructure(
        targetLayoutMatrix,
        targetLayoutStyles
      );

      // Then let's iterate over all the cell children and either:
      // - update its local styles
      // - or remove it
      const updatedCells = children.reduce<Array<any>>((cells, cellElement) => {
        // If a cell is hidden, add it back so it doesn't get removed
        if (!layoutStructure[cellElement.id] && isCellHidden(cellElement)) {
          layoutStructure[cellElement.id] = {
            styles: PLACEHOLDER_STYLES,
            position: children.indexOf(cellElement),
          };
        }

        const cellData = layoutStructure[cellElement.id];
        if (cellData) {
          cells.push(
            updateElementWithStyles(
              cellElement,
              cellData.styles,
              mediaQuery,
              variantState
            )
          );
        } else {
          // if we are in this block, then the cell is not in the new matrix
          if (selectedElementContainsSlot(cellElement)) {
            // Collect for slot field removal
            cellsWithSlotsToRemove.push(cellElement);
          }
        }

        return cells;
      }, []);

      // Then let's create any new cells (if any). We create new cells when:
      // - unmerging a cell into N 1x1 cells
      // - adding new rows and columns
      for (const [cellId, cellData] of Object.entries(layoutStructure)) {
        if (cellId.startsWith(NEW_CELL_PREFIX)) {
          const newCell = createEmptyCell();
          if (!newCell) {
            reportError('Could not create new Cell');
            continue;
          }

          updatedCells.push(
            // @ts-expect-error - TS18048 - 'cellData' is possibly 'undefined'.
            updateElementWithStyles(newCell, cellData.styles, mediaQuery)
          );
          layoutStructure[newCell.id] = cellData;
        }
      }

      if (updatedCells.length === 0) {
        // Something went wrong, let's abort and return the original layoutElement
        reportError('Layout has no children after update');
        return nextLayoutElement;
      }

      // Finally, let's sort the cells based on the computed structure
      const sortedCells = sortLayoutCells(updatedCells, layoutStructure);

      const nextElementData = Expressions.setIn(
        Expressions.getElementData(nextLayoutElement),
        [{in: 'Record', at: 'children'}],
        EList(sortedCells.map(EElement))
      );

      return Expressions.setElementData(nextLayoutElement, nextElementData);
    },
    render
  );

  if (nextRender === render) {
    // we did not update render, so no need to update the component map either
    return componentMap;
  }

  if (cellsWithSlotsToRemove.length > 0) {
    let updatedComponent = Component.update(component, {
      render: nextRender,
    });

    for (const cellElement of cellsWithSlotsToRemove) {
      // if the cell contains a slot,
      // we need to remove the corresponding slot field from the component data type
      // and clean up any references to the removed slot property in the component render
      updatedComponent = handleSlotFieldRemoval(
        cellElement,
        updatedComponent,
        plugins
      );
    }
    return componentMap.set(componentType, updatedComponent);
  }

  return updateComponentRenderInComponentMap(
    componentMap,
    componentType,
    nextRender
  );
};

/**
 * Sorts Layout Cells children based on their position in the given layout structure
 */
function sortLayoutCells(
  cells: ExpressionElement[],
  layoutStructure: LayoutStructure
): ExpressionElement[] {
  return cells.sort((cell1, cell2) => {
    const cell1Data = layoutStructure[cell1.id];
    const cell2Data = layoutStructure[cell2.id];
    if (!cell1Data || !cell2Data) {
      return 0;
    }
    return cell1Data.position - cell2Data.position;
  });
}

/**
 * Updates a Cell or Layout element with local styles
 */
function updateElementWithStyles(
  element: ExpressionElement,
  styles: WebflowCssProperties,
  mediaQuery: BreakpointID,
  variantState: VariantStateKey | null
): ExpressionElement {
  // Here we're forcing noPseudo because cell local spans styles cannot be
  // changed for different pseudo states
  return updateElementLocalStyle(
    styles,
    mediaQuery,
    NO_PSEUDO,
    variantState
  )(element);
}

/**
 * Creates a new empty Cell element
 */
function createEmptyCell() {
  return Expressions.getElement(
    EElement({
      id: generateGuid(),
      type: qualify(Plugins.name('Layout'), Component.name('Cell')),
      data: ERecord({
        children: EList([]),
      }),
    })
  );
}

function reportError(reason: string) {
  reportBug('LayoutError', `Failed to merge cells: ${reason}`, {
    team: 'structure',
    ticket: 'https://webflow.atlassian.net/browse/ACT-1628',
    method: 'updateLayoutRenderInComponentMap',
  });
}

export default updateLayoutRenderInComponentMap;
